import React from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';
import InnerPageHeader from '../components/InnerPageHeader';
import {
  Emphasis,
  InnerCard,
  InnerContainer,
  InnerSection,
  PageTitle,
  Paragraph,
  SectionTitle,
} from '../components/InnerPageLayout';

/* ─── Data ───────────────────────────────────────────────────────────────────── */

const BELIEFS = [
  { title: 'Simplicity beats flexibility',    text: "The best AI products don't expose complexity, they remove it." },
  { title: 'Products over prompts',           text: 'Great experiences come from thoughtful design, not endless configuration.' },
  { title: 'Privacy is non-negotiable',       text: 'Personal data belongs to users. Always.' },
  { title: 'One problem. One clear solution.', text: 'Each product we build has a focused purpose and a clear audience.' },
];

const PRIVACY_PILLARS = [
  { title: 'Secure transmission',       text: 'All data travels over encrypted connections. Nothing is sent in plaintext.' },
  { title: 'No human access',           text: 'Personal conversations and context are never accessed by Chots employees.' },
  { title: 'No training on your data',  text: 'Private user content is never used to train or fine-tune AI models.' },
  { title: 'Architecture-first',        text: 'Access is constrained by how systems are built, not by policy alone.' },
];

const VALUES = [
  { title: 'Simplicity',          text: 'We remove complexity instead of adding options. A product should feel clear and intuitive without explanation.' },
  { title: 'Focus',               text: 'Each product solves a specific problem for a specific audience. We avoid feature sprawl and unnecessary abstraction.' },
  { title: 'Privacy by Design',   text: 'User data belongs to users. We treat privacy as an engineering requirement, not a legal checkbox.' },
  { title: 'Responsibility',      text: 'AI influences decisions, emotions, and learning. We take that seriously and design with care.' },
  { title: 'Long-Term Thinking',  text: 'We optimize for durability and trust, not short-term metrics or hype.' },
];

/* ─── Shared grid ────────────────────────────────────────────────────────────── */

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-top: 4px;
`;

const Tile = styled.div<{ $accent?: boolean }>`
  border-radius: 14px;
  padding: 18px;
  background: ${({ $accent }) =>
    $accent
      ? 'rgba(129, 127, 253, 0.07)'
      : 'rgba(239, 244, 249, 0.55)'};
  border: 1px solid ${({ $accent }) =>
    $accent ? 'rgba(129, 127, 253, 0.18)' : '#dde6f0'};
`;

const TileTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1f2b3a;
  margin-bottom: 5px;
`;

const TileText = styled.div`
  font-size: 13px;
  color: #4a5a6b;
  line-height: 1.6;
`;

/* ─── Approach steps ─────────────────────────────────────────────────────────── */

const StepsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 16px 0 4px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #0f1c2d;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2b3a;
`;

const StepDetail = styled.div`
  font-size: 13px;
  color: #4a5a6b;
  line-height: 1.55;
`;

/* ─── Values list ────────────────────────────────────────────────────────────── */

const ValueRow = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid rgba(31, 43, 58, 0.07);

  &:first-child { padding-top: 0; }
  &:last-child  { border-bottom: none; padding-bottom: 0; }
`;

const ValueTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2b3a;
  margin-bottom: 4px;
`;

const ValueText = styled.div`
  font-size: 13px;
  color: #4a5a6b;
  line-height: 1.6;
`;

/* ─── Product mini-cards ─────────────────────────────────────────────────────── */

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
  margin-top: 4px;
`;

const ProductMini = styled.div<{ $featured?: boolean }>`
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: ${({ $featured }) =>
    $featured ? 'linear-gradient(140deg, #0a1420, #1c3650)' : 'rgba(239, 244, 249, 0.6)'};
  border: 1px solid ${({ $featured }) =>
    $featured ? 'rgba(129, 127, 253, 0.25)' : '#cfd9e4'};
`;

const ProductMiniName = styled.div<{ $featured?: boolean }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ $featured }) => ($featured ? '#ffffff' : '#1f2b3a')};
`;

const ProductMiniTagline = styled.div<{ $featured?: boolean }>`
  font-size: 13px;
  color: ${({ $featured }) => ($featured ? 'rgba(255,255,255,0.52)' : '#4a5a6b')};
  line-height: 1.5;
`;

const ProductMiniStatus = styled.div`
  font-size: 12px;
  color: #6b7c90;
  font-style: italic;
  margin-top: 2px;
`;

const ProductMiniLink = styled.a<{ $featured?: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  margin-top: 6px;
  color: ${({ $featured }) => ($featured ? '#817ffd' : '#0f1c2d')};

  &:hover { text-decoration: underline; }
`;

/* ─── Card stack ─────────────────────────────────────────────────────────────── */

const CardStack = styled.div`
  display: grid;
  gap: 24px;
`;

/* ─── Component ──────────────────────────────────────────────────────────────── */

const About: React.FC = () => (
  <>
    <InnerPageHeader
      data={{
        title: 'About Chots',
        paragraph: 'Human-centered AI applications built to feel simple, purposeful, and safe.',
      }}
    />
    <InnerSection>
      <InnerContainer>
        <CardStack>

          {/* ── Who We Are ── */}
          <InnerCard>
            <PageTitle>Who We Are</PageTitle>
            <Paragraph>
              Chots is a product company focused on building human-centered AI applications.
            </Paragraph>
            <Paragraph>
              We believe AI should feel simple, purposeful, and safe — without requiring technical
              knowledge or constant configuration. Our goal is to create products that quietly
              improve everyday life while respecting the people who use them.
            </Paragraph>
            <SectionTitle>Our Mission</SectionTitle>
            <Paragraph>
              AI capabilities have grown rapidly, but for most people, using AI still feels
              complicated, fragmented, or untrustworthy. Many tools expose users to complexity
              instead of shielding them from it.
            </Paragraph>
            <Paragraph>
              Chots was created to take a different approach: to build AI products that are{' '}
              <Emphasis>useful first</Emphasis>, <Emphasis>carefully designed</Emphasis>, and{' '}
              <Emphasis>aligned with real human needs</Emphasis>.
            </Paragraph>
          </InnerCard>

          {/* ── Our Approach ── */}
          <InnerCard>
            <PageTitle>Our Approach to AI</PageTitle>
            <Paragraph>
              We don't build general-purpose tools and ask users to adapt. Every product starts
              from the same three steps:
            </Paragraph>
            <StepsRow>
              <Step>
                <StepBadge>1</StepBadge>
                <StepLabel>Define the problem</StepLabel>
                <StepDetail>Start with a clearly scoped human need — not a feature idea or a technology looking for a use case.</StepDetail>
              </Step>
              <Step>
                <StepBadge>2</StepBadge>
                <StepLabel>Design the experience</StepLabel>
                <StepDetail>Build an opinionated, focused interface before touching any AI layer.</StepDetail>
              </Step>
              <Step>
                <StepBadge>3</StepBadge>
                <StepLabel>Apply AI where it matters</StepLabel>
                <StepDetail>Use AI only where it meaningfully improves the outcome for the user.</StepDetail>
              </Step>
            </StepsRow>
            <Paragraph>
              For us, AI is an enabling technology — not the product itself.
            </Paragraph>
          </InnerCard>

          {/* ── Privacy & Responsibility ── */}
          <InnerCard>
            <PageTitle>Privacy & Responsibility</PageTitle>
            <Paragraph>
              Privacy is not a feature at Chots. It is a foundation.
            </Paragraph>
            <TileGrid>
              {PRIVACY_PILLARS.map((p) => (
                <Tile key={p.title} $accent>
                  <TileTitle>{p.title}</TileTitle>
                  <TileText>{p.text}</TileText>
                </Tile>
              ))}
            </TileGrid>
            <Paragraph>
              Trust is earned through how systems are built, not how they are marketed.
            </Paragraph>
          </InnerCard>

          {/* ── What We Believe ── */}
          <InnerCard>
            <PageTitle>What We Believe</PageTitle>
            <Paragraph>
              Principles that keep our products grounded in real human needs.
            </Paragraph>
            <TileGrid>
              {BELIEFS.map((b) => (
                <Tile key={b.title}>
                  <TileTitle>{b.title}</TileTitle>
                  <TileText>{b.text}</TileText>
                </Tile>
              ))}
            </TileGrid>
          </InnerCard>

          {/* ── Our Values ── */}
          <InnerCard>
            <PageTitle>Our Values</PageTitle>
            {VALUES.map((v) => (
              <ValueRow key={v.title}>
                <ValueTitle>{v.title}</ValueTitle>
                <ValueText>{v.text}</ValueText>
              </ValueRow>
            ))}
          </InnerCard>

          {/* ── What We're Building ── */}
          <InnerCard>
            <PageTitle>What We're Building</PageTitle>
            <Paragraph>
              A growing ecosystem of focused AI products — each built around a clear use case and
              the same shared principles: clarity, privacy, and thoughtful design.
            </Paragraph>
            <ProductGrid>
              <ProductMini $featured>
                <ProductMiniName $featured>Chot Buddies</ProductMiniName>
                <ProductMiniTagline $featured>Private AI companions for everyday life</ProductMiniTagline>
                <ProductMiniLink href="/products/chot-buddies/" $featured>View product →</ProductMiniLink>
              </ProductMini>
              <ProductMini>
                <ProductMiniName>Chot Study</ProductMiniName>
                <ProductMiniTagline>Turn your study materials into interactive learning</ProductMiniTagline>
                <ProductMiniStatus>Launching soon</ProductMiniStatus>
                <ProductMiniLink href="/products/chot-study/">View product →</ProductMiniLink>
              </ProductMini>
            </ProductGrid>
          </InnerCard>

          {/* ── Building in Public + Looking Forward ── */}
          <InnerCard>
            <PageTitle>Building in Public</PageTitle>
            <Paragraph>
              Chots is being built transparently and deliberately. We openly share product
              direction, design decisions, trade-offs, and lessons learned — because building in
              public keeps us accountable and grounded in real user needs, not just assumptions.
            </Paragraph>
            <SectionTitle>Looking Forward</SectionTitle>
            <Paragraph>
              We believe the future of AI is quiet, helpful, and embedded naturally into everyday
              tools — calm instead of overwhelming, useful instead of flashy, trustworthy instead
              of intrusive.
            </Paragraph>
            <Paragraph>Chots is just getting started.</Paragraph>
          </InnerCard>

        </CardStack>
      </InnerContainer>
    </InnerSection>
    <Footer />
  </>
);

export default About;
